import { defineStore } from "pinia";
import { useStorage, useDebounceFn } from "@vueuse/core";

import { useSearchStore } from "@/stores/search";

// GraphQL
import { provideApolloClient } from "@vue/apollo-composable";
import apolloClient from "@/graphql/client";
import { useMutation } from "@vue/apollo-composable";
import gql from "graphql-tag";

provideApolloClient(apolloClient);

// Upload a single document
const { mutate: uploadeAccountingFile } = useMutation(
  gql`
    mutation uploadAccountingFile($data: AccountingDocumentCreateInput!) {
      createAccountingDocument(data: $data) {
        id
      }
    }
  `
);

let reloadDocumentsDebounced = null;

export const useFinanceStore = defineStore("finance", {
  state: () => ({
    documents: useStorage("financeDocuments", []),
    documentsTotal: 0,
    newDocuments: [],
    loading: false,
    take: 25,
    skip: 0,
    typeFilters: [],
    yearFilters: [],
    tagFilters: [],
    monthFilters: [],
    selectedDocuments: [],
    sortMethod: "date", // "date" | "createdAt"
  }),
  getters: {
    allNewDocuments: (state) => state.newDocuments || [],
    checkIfNewDocumentsAreComplete: (state) => {
      let complete = true;
      state.newDocuments.forEach((doc) => {
        //console.log("doc", doc);
        Object.values(doc.data).some((value) => {
          if (!value) {
            complete = false;
          }
        });
      });
      //console.log("checkIfNewDocumentsAreComplete", complete);
      return complete;
    },
    // Documents are already filtered server-side via GraphQL where
    filteredDocuments: (state) => state.documents,
    canLoadMore: (state) => {
      if (state.documentsTotal > state.documents.length) {
        return true;
      }
      return false;
    },
    restOfDocumentsToLoad: (state) => {
      return state.documentsTotal - state.documents.length;
    },
  },
  actions: {
    scheduleReloadDocuments() {
      if (!reloadDocumentsDebounced) {
        reloadDocumentsDebounced = useDebounceFn(() => {
          this.getDocuments(true);
        }, 300);
      }
      reloadDocumentsDebounced();
    },
    async getDocuments(init = false) {
      // Allow init reloads (filters/company/sort) to supersede an in-flight load-more
      if (this.loading && init !== true) return;

      if (init === true) {
        this.skip = 0;
      }

      this.selectedDocuments = [];
      this.loading = true;

      try {
        const { where: companyWhere } = useSearchStore().getAccountingFilter;

        const where = {
          AND: [
            companyWhere,
            { type: { in: this.typeFilters } },
            this.yearFilters.length > 0
              ? {
                  OR: this.yearFilters.map((year) => ({
                    date: { gte: `${year}-01-01`, lte: `${year}-12-31` },
                  })),
                }
              : { id: { in: [] } },
            { tag: { in: [...this.tagFilters, ...this.monthFilters] } },
          ],
        };

        // Build orderBy clause based on sortMethod
        const orderByClause = this.sortMethod === "createdAt"
          ? [{ createdAt: "desc" }]
          : [{ date: "desc" }, { tag: "desc" }, { type: "asc" }];

        // use apollo client directly
        const result = await apolloClient.query({
          query: gql`
            query AppFinanceDocuments(
              $where: AccountingDocumentWhereInput!
              $take: Int
              $skip: Int!
              $orderBy: [AccountingDocumentOrderByInput!]
            ) {
              accountingDocuments(
                where: $where
                take: $take
                skip: $skip
                orderBy: $orderBy
              ) {
                id
                type
                date
                tag
                company {
                  id
                }
                updateAt
                createdAt
              }
              accountingDocumentsCount(where: $where)
            }
          `,
          variables: {
            where,
            take: this.take,
            skip: this.skip,
            orderBy: orderByClause,
          },
          fetchPolicy: "no-cache",
        });
        //console.dir(result);
        if (result.data && result.data.accountingDocuments) {
          if (result.data.accountingDocuments.length > 0) {
            if (init === true) {
              this.documents = result.data.accountingDocuments;
              this.documentsTotal = result.data.accountingDocumentsCount;
            } else {
              this.documents.push(...result.data.accountingDocuments);
            }
            this.loading = false;
            this.skip += this.take;
            return { code: "SUCCESS", message: "Load complete" };
          }
          this.documents = [];
          this.documentsTotal = 0;
          this.loading = false;
          this.documentsTotal = 0;
          return { code: "SUCCESS", message: "No documents found" };
        }
        console.dir(result);
        this.loading = false;
        this.documentsTotal = 0;
        return { code: "ERROR", message: "Error while loading documents" };
      } catch (e) {
        console.dir(e);
        this.loading = false;
        this.documentsTotal = 0;
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    addNewDocuments(files) {
      const skippedFilesWithNames = [];
      for (let i = 0; i < files.length; i++) {
        const newDoc = {
          id: files[i].name + files[i].size + files[i].lastModified,
          data: {
            file: files[i],
            company: null,
            date: null,
            type: null,
            tag: null,
          },
        };
        // Only add if file is not already in the list (checking filename, size and lastModified)
        const alreadyAdded = this.newDocuments.filter((doc) => doc.id === newDoc.id);
        //console.log("alreadyAdded", alreadyAdded);
        //console.log(newDoc.id);
        if (!typeof alreadyAdded === "undefined" || alreadyAdded.length === 0) {
          this.newDocuments.push(newDoc);
        } else {
          skippedFilesWithNames.push(files[i].name);
        }
      }
      return skippedFilesWithNames;
    },
    updateDataOnKeyForNewDocumentUsingId(id, data, key) {
      const doc = this.newDocuments.find((doc) => doc.id === id);
      if (doc) {
        doc.data[key] = data;
      }
    },
    getValueForNewDocumentUsingId(id, key) {
      const doc = this.newDocuments.find((doc) => doc.id === id);
      if (doc) {
        return doc.data[key];
      }
      return null;
    },
    removeNewFiles(files) {
      for (let i = 0; i < files.length; i++) {
        const docIndex = this.newDocuments.findIndex((doc) => doc.id === files[i]);
        if (docIndex >= 0) {
          this.newDocuments.splice(docIndex, 1);
        }
      }
    },
    async uploadNewDocuments() {
      if (!this.checkIfNewDocumentsAreComplete) return;
      const uploadedIds = [];

      // Upload single file after another
      for (let i = 0; i < this.newDocuments.length; i++) {
        const element = this.newDocuments[i];
        const mutationData = {
          attachment: { upload: element.data.file },
          type: element.data.type.value,
          date: element.data.date.value,
          tag: element.data.tag.name,
          company: { connect: { id: element.data.company.id } },
        };
        try {
          const result = await uploadeAccountingFile({ data: mutationData });
          //console.log(result);
          if (result.data?.createAccountingDocument?.id) {
            uploadedIds.push(element.id);
          }
        } catch (e) {
          console.dir(e);
        }
      }
      // Remove files that were successfully uploaded
      //console.log("uploadedIndexes (needed to remove)", uploadedIds);
      this.removeNewFiles(uploadedIds);

      // Update Store with new data
      await this.getDocuments(true);
    },
    resetData() {
      this.documents = [];
      this.newDocuments = [];
    },
  },
});
